import { Link, NavLink, Navigate, Outlet, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import { Brand } from '../../components/Brand.tsx';
import { StatusStrip } from '../../components/page.tsx';
import { ThemeSwitch } from '../../components/Topbar.tsx';
import { Button, ComingSoon, Splash } from '../../components/ui.tsx';
import { api } from '../../lib/api.ts';
import { homePath, useLogout, useMe } from '../../lib/auth.ts';
import { ADMIN_SECTIONS } from './sections.ts';
import { FeedbackAdmin } from './FeedbackAdmin.tsx';
import s from './admin.module.css';

/** Ylläpito: ylhäällä header, sen alla päävälilehdet ja kortin sisällä alavälilehdet. */
export function AdminShell() {
  const { data: me, isLoading } = useMe();
  const logout = useLogout();

  if (isLoading) return <Splash />;
  if (!me?.isAdmin) return <Navigate to="/" replace />;

  const hasAppView = me.roles.landlord || me.roles.tenant;

  return (
    <div className={s.page}>
      <header className={s.header}>
        <div className={s.headerBrand}>
          <Brand to="/admin" />
          <span className={s.badge}>Ylläpito</span>
        </div>
        <div className={s.headerCenter}>{me.name ?? me.email}</div>
        <div className={s.headerRight}>
          {hasAppView && (
            <Link
              to={homePath({ ...me, isAdmin: false })}
              style={{ fontWeight: 600, marginRight: 8 }}
            >
              Sovellukseen
            </Link>
          )}
          <ThemeSwitch />
          <Button variant="ghost" onClick={() => logout.mutate()}>
            <LogOut size={17} aria-hidden="true" />
            Kirjaudu ulos
          </Button>
        </div>
      </header>

      <main className={s.content}>
        <h1 className={s.title}>Ylläpito</h1>
        <nav className={s.mainTabs} aria-label="Ylläpidon osiot">
          {ADMIN_SECTIONS.map(({ key, label, icon: Icon }) => (
            <NavLink
              key={key}
              to={key ? `/admin/${key}` : '/admin'}
              end={!key}
              className={({ isActive }) => `${s.mainTab} ${isActive ? s.mainTabActive : ''}`}
            >
              <Icon size={17} aria-hidden="true" />
              {label}
            </NavLink>
          ))}
        </nav>
        <Outlet />
      </main>
    </div>
  );
}

export function AdminOverview() {
  const { data } = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () =>
      api<{ users: number; landlords: number; tenants: number; properties: number }>(
        '/admin/overview',
      ),
  });
  return (
    <div className={s.card} style={{ paddingTop: '1.5rem' }}>
      <StatusStrip
        items={[
          { label: 'Käyttäjät', value: data?.users ?? '–' },
          { label: 'Vuokranantajat', value: data?.landlords ?? '–' },
          { label: 'Vuokralaiset', value: data?.tenants ?? '–' },
          { label: 'Asunnot', value: data?.properties ?? '–' },
        ]}
        note="Maksavat asiakkaat ja virheet lisätään, kun tilaukset ja virheloki on rakennettu."
      />
    </div>
  );
}

/** Osio alavälilehtineen. Sisältö rakennetaan osio kerrallaan. */
export function AdminSectionPage() {
  const { section = '', sub } = useParams();
  const config = ADMIN_SECTIONS.find((x) => x.key === section);
  if (!config || config.subs.length === 0) return <Navigate to="/admin" replace />;
  const current = config.subs.find((x) => x.key === sub);
  if (!current) return <Navigate to={`/admin/${section}/${config.subs[0]!.key}`} replace />;

  return (
    <div className={s.card}>
      <nav className={s.subTabs} aria-label={config.label}>
        {config.subs.map((x) => (
          <NavLink
            key={x.key}
            to={`/admin/${section}/${x.key}`}
            className={({ isActive }) => `${s.subTab} ${isActive ? s.subTabActive : ''}`}
          >
            {x.label}
          </NavLink>
        ))}
      </nav>
      {section === 'tuki' && sub === 'palautteet' ? (
        <FeedbackAdmin types={['feedback', 'question']} />
      ) : section === 'tuki' && sub === 'bugit' ? (
        <FeedbackAdmin types={['bug']} />
      ) : (
        <ComingSoon title={current.label} text={current.text} />
      )}
    </div>
  );
}
